import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, KgObservabilityDashboard, KgAnomalyScanResult, KgSyncEventRow } from '../../lib/api'
import { Activity, AlertTriangle, CheckCircle, XCircle, Database, RefreshCw, Search, Zap, Clock, ArrowRight } from 'lucide-react'
import { KaijuLoader } from '../KaijuLoader'

function SeverityBadge({ severity }: { severity: string }) {
  const colors: Record<string, string> = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs border ${colors[severity] || colors.info}`}>
      {severity}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    success: 'bg-green-500/20 text-green-400',
    failure: 'bg-red-500/20 text-red-400',
    partial: 'bg-yellow-500/20 text-yellow-400',
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${colors[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status}
    </span>
  )
}

function LagBadge({ lagSeconds }: { lagSeconds: number | null }) {
  if (lagSeconds === null) return <span className="text-gray-500 text-xs">No data</span>
  const color = lagSeconds < 3600 ? 'text-green-400' : lagSeconds < 86400 ? 'text-yellow-400' : 'text-red-400'
  const label = lagSeconds < 60 ? `${lagSeconds}s` : lagSeconds < 3600 ? `${Math.round(lagSeconds / 60)}m` : `${Math.round(lagSeconds / 3600)}h`
  return <span className={`text-xs font-mono ${color}`}>{label}</span>
}

export default function KgObservabilityPanel() {
  const queryClient = useQueryClient()
  const [scanResult, setScanResult] = useState<KgAnomalyScanResult | null>(null)
  const [eventsPage, setEventsPage] = useState(0)

  // Health dashboard (auto-refresh 30s)
  const { data: health, isLoading: healthLoading } = useQuery<KgObservabilityDashboard>({
    queryKey: ['kg-health'],
    queryFn: () => api.kg.health(),
    refetchInterval: 30000,
  })

  // Events
  const { data: eventsData } = useQuery({
    queryKey: ['kg-events', eventsPage],
    queryFn: () => api.kg.events({ limit: 10, offset: eventsPage * 10 }),
  })

  // Anomaly scan mutation
  const scanMutation = useMutation({
    mutationFn: () => api.kg.scanAnomalies(),
    onSuccess: (data) => setScanResult(data),
  })

  // Backfill mutation
  const backfillMutation = useMutation({
    mutationFn: () => api.kg.backfill(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kg-health'] })
      queryClient.invalidateQueries({ queryKey: ['kg-events'] })
    },
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Database className="w-5 h-5 text-purple-400" />
        <h2 className="text-lg font-semibold text-gray-200">Knowledge Graph Observability</h2>
      </div>

      {healthLoading ? (
        <div className="flex items-center gap-2 text-gray-400">
          <KaijuLoader size="sm" />
          Loading health data...
        </div>
      ) : !health ? (
        <p className="text-gray-500 text-sm">Could not load KG health data. Is Neo4j running?</p>
      ) : (
        <div className="space-y-4">
          {/* Connection Health */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Connection
              </h3>
              {health.connection.connected ? (
                <span className="flex items-center gap-1 text-green-400 text-xs">
                  <CheckCircle className="w-3.5 h-3.5" /> Connected
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-400 text-xs">
                  <XCircle className="w-3.5 h-3.5" /> Disconnected
                </span>
              )}
            </div>
            {health.connection.connected && (
              <div className="grid grid-cols-3 gap-4 text-xs text-gray-400">
                <div>Version: <span className="text-gray-200">{health.connection.serverVersion}</span></div>
                <div>Address: <span className="text-gray-200">{health.connection.address}</span></div>
                <div>Latency: <span className="text-gray-200">{health.connection.latencyMs}ms</span></div>
              </div>
            )}
          </div>

          {/* Node/Relationship Counts */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
              <Database className="w-4 h-4" />
              Graph Contents
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Nodes</p>
                <div className="space-y-1">
                  {health.nodeCounts.nodes.map(n => (
                    <div key={n.label} className="flex justify-between text-xs">
                      <span className="text-gray-400">{n.label}</span>
                      <span className="text-gray-200 font-mono">{n.count.toLocaleString()}</span>
                    </div>
                  ))}
                  {health.nodeCounts.nodes.length === 0 && <p className="text-xs text-gray-500">No nodes</p>}
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Relationships</p>
                <div className="space-y-1">
                  {health.nodeCounts.relationships.map(r => (
                    <div key={r.type} className="flex justify-between text-xs">
                      <span className="text-gray-400">{r.type}</span>
                      <span className="text-gray-200 font-mono">{r.count.toLocaleString()}</span>
                    </div>
                  ))}
                  {health.nodeCounts.relationships.length === 0 && <p className="text-xs text-gray-500">No relationships</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Sync Lag */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Sync Lag
            </h3>
            <div className="space-y-2">
              {health.syncLag.map(lag => (
                <div key={lag.entityType} className="flex items-center justify-between text-xs">
                  <span className="text-gray-400 capitalize">{lag.entityType}</span>
                  <div className="flex items-center gap-3">
                    <LagBadge lagSeconds={lag.lagSeconds} />
                    {lag.staleCount > 0 && (
                      <span className="text-yellow-400">{lag.staleCount} stale</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Anomaly Scanner */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Anomaly Scanner
              </h3>
              <button
                onClick={() => scanMutation.mutate()}
                disabled={scanMutation.isPending}
                className="flex items-center gap-1 px-3 py-1 text-xs bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 rounded text-white transition"
              >
                {scanMutation.isPending ? <KaijuLoader size="sm" /> : <AlertTriangle className="w-3 h-3" />}
                {scanMutation.isPending ? 'Scanning...' : 'Run Scan'}
              </button>
            </div>
            {scanResult && (
              <div className="space-y-2">
                <div className="flex gap-3 text-xs">
                  <span className="text-gray-400">Scanned in {scanResult.durationMs}ms</span>
                  <span className="text-red-400">{scanResult.summary.critical} critical</span>
                  <span className="text-yellow-400">{scanResult.summary.warning} warning</span>
                  <span className="text-blue-400">{scanResult.summary.info} info</span>
                </div>
                {scanResult.anomalies.length === 0 ? (
                  <p className="text-green-400 text-xs flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> No anomalies detected
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {scanResult.anomalies.slice(0, 20).map((a, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs py-1 border-b border-gray-700/50">
                        <SeverityBadge severity={a.severity} />
                        <div className="flex-1">
                          <p className="text-gray-300">{a.description}</p>
                          {a.suggestedFix && <p className="text-gray-500 mt-0.5">{a.suggestedFix}</p>}
                        </div>
                      </div>
                    ))}
                    {scanResult.anomalies.length > 20 && (
                      <p className="text-gray-500 text-xs">... and {scanResult.anomalies.length - 20} more</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sync Event Log */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Recent Sync Events
            </h3>
            {eventsData && eventsData.events.length > 0 ? (
              <div className="space-y-1">
                <div className="grid grid-cols-5 gap-2 text-xs text-gray-500 pb-1 border-b border-gray-700">
                  <span>Type</span>
                  <span>Entity</span>
                  <span>Status</span>
                  <span>Duration</span>
                  <span>Time</span>
                </div>
                {eventsData.events.map((evt: KgSyncEventRow) => (
                  <div key={evt.id} className="grid grid-cols-5 gap-2 text-xs py-1 border-b border-gray-700/30">
                    <span className="text-gray-300">{evt.event_type}</span>
                    <span className="text-gray-400">{evt.entity_type}</span>
                    <StatusBadge status={evt.status} />
                    <span className="text-gray-400 font-mono">{evt.duration_ms ? `${evt.duration_ms}ms` : '-'}</span>
                    <span className="text-gray-500">{new Date(evt.created_at).toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs text-gray-500">{eventsData.total} total events</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEventsPage(Math.max(0, eventsPage - 1))}
                      disabled={eventsPage === 0}
                      className="text-xs text-gray-400 hover:text-gray-200 disabled:text-gray-600"
                    >
                      Prev
                    </button>
                    <button
                      onClick={() => setEventsPage(eventsPage + 1)}
                      disabled={(eventsPage + 1) * 10 >= eventsData.total}
                      className="text-xs text-gray-400 hover:text-gray-200 disabled:text-gray-600"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500">No sync events recorded yet.</p>
            )}
          </div>

          {/* Backfill Trigger */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Full Backfill
                </h3>
                <p className="text-xs text-gray-500 mt-1">Re-sync all entities from PostgreSQL to Neo4j</p>
              </div>
              <button
                onClick={() => backfillMutation.mutate()}
                disabled={backfillMutation.isPending}
                className="flex items-center gap-1 px-3 py-1 text-xs bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 rounded text-white transition"
              >
                {backfillMutation.isPending ? <KaijuLoader size="sm" /> : <ArrowRight className="w-3 h-3" />}
                {backfillMutation.isPending ? 'Running...' : 'Run Backfill'}
              </button>
            </div>
            {backfillMutation.isSuccess && backfillMutation.data && (
              <p className="text-xs text-green-400 mt-2">{backfillMutation.data.message}</p>
            )}
            {backfillMutation.isError && (
              <p className="text-xs text-red-400 mt-2">Backfill failed: {(backfillMutation.error as Error).message}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
